import { PkBadge } from '@uefadigital/panenka-react';
import clsx from 'clsx';

import {
  getStatisticsData,
  getHowScoredData,
  getDataByName,
  getGoalCoordinates,
  getStatByName,
  getFallBackImage,
  getLogoImage,
} from '../../utils/misc';
import ChartAttempts from '../ChartAttempts';
import EnhancedStatisticsModule from '../EnhancedStatisticsModule';
import HowTheyScored from '../HowTheyScored';

import { useWindowSize } from '@hooks/useWindowSize';
import { useTranslateFn } from '@shared/Translate';
import { formatUrl } from '@utilities/fetch';

import * as styles from './styles.module.scss';

export const PlayerData = ({ playerData, playerInfo, options, goalData }) => {
  const t = useTranslateFn();
  const { isDesktop } = useWindowSize();
  const fallBackImage = getFallBackImage(playerInfo, options);
  const logoUrl = getLogoImage(playerData, options);
  if (!playerData || !playerData.player) return null;
  const donutData = getStatisticsData(playerData);

  const goalsValue = getStatByName(playerInfo, 'goals');
  const assistsValue = getStatByName(playerInfo, 'assists');
  const minutesValue = getStatByName(playerInfo, 'minutes_played_official');

  const matchesData = getDataByName(playerData, 'matches_appearance');
  const avgGoalData = getDataByName(playerData, 'goals', 'averagePerMatch');

  const goalCoordinates = getGoalCoordinates(goalData);
  const howTheyScoredData = getHowScoredData(playerData);
  const hideHowTheyScored = options?.hideHowTheyScored || !howTheyScoredData || !howTheyScoredData.length;

  const teamServiceUrl = formatUrl(null, `/api/v1/linkrules/team/${playerInfo?.team?.id}/`, {
    competitionId: options.cup,
    phase: options.phase,
  });

  const playerServiceUrl = formatUrl(null, `/api/v1/linkrules/player/${playerData.player.id}/`, {
    competitionId: options.cup,
    phase: options.phase,
  });

  let modulesNumber = 0;
  if (!options?.hideAttempts && donutData && donutData.length) modulesNumber++;
  if (!hideHowTheyScored) modulesNumber++;
  if (!options?.hidePosition && matchesData && avgGoalData && goalCoordinates) modulesNumber++;

  const gridSize = 12 / modulesNumber;

  return (
    <div className={styles.tsPlayerStats}>
      <div className={styles.tsPlayerStatsContainer}>
        <div className={clsx(styles.tsPlayerStatsWrap, 'ts-player-stats-wrap', 'pk-theme--light')}>
          <div className={styles.tsPlayerStatsHeader}>
            <div className={styles.tsPlayerStatsPlayer}>
              <a href={teamServiceUrl} title={playerData?.team?.displayName}>
                <PkBadge
                  alt={playerData?.team?.displayName}
                  title={playerData?.team?.displayName}
                  fallbackImage={fallBackImage}
                  src={logoUrl}
                  size={isDesktop ? 72 : 40}
                />
              </a>
              <div className={styles.tsPlayerInfo}>
                <a href={playerServiceUrl} title={playerData.player.displayName}>
                  <div className={styles.tsPlayerName}>
                    <span className={styles.tsPlayerFirstName}>{playerData.player.firstName}</span>
                    <span>{playerData.player.lastName}</span>
                  </div>
                </a>
                <a href={teamServiceUrl} title={playerData?.team?.displayName}>
                  <div className={styles.tsPlayerTeam}>{playerInfo?.team?.displayName}</div>
                </a>
              </div>
            </div>
            <div className={styles.tsStats}>
              <div className={styles.tsStatsContainer}>
                <span className={styles.tsStatsGoals}>{playerInfo && goalsValue}</span>
                <span className={styles.tsStatsLabel}>
                  {t(playerInfo && `${goalsValue > 1 ? 'stats_goals' : 'stats_goal'}`)}
                </span>
              </div>
              {options?.enableEnhancedStatistics && (
                <>
                  {!!assistsValue && (
                    <div className={styles.tsStatsContainer}>
                      <span className={styles.tsStatsAssists}>{assistsValue}</span>
                      <span className={styles.tsStatsLabel}>
                        {t(`${+assistsValue === 1 ? 'uefa_assist' : 'stats_Assists'}`)}
                      </span>
                    </div>
                  )}
                  {!options.isFutsal && !!minutesValue && (
                    <div className={styles.tsStatsContainer}>
                      <span className={styles.tsStatsMinutes}>{minutesValue}</span>
                      <span className={styles.tsStatsLabel}>{t('stats_minutes')}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {!!modulesNumber && (
            <div className={clsx(styles.tsStatsCardWrap, 'pk-container')}>
              {!options?.hideAttempts && (
                <div className={clsx(styles.tsStatsCard, `pk-col pk-col--span-md-${gridSize} pk-col--span-12`)}>
                  <ChartAttempts data={donutData} />
                </div>
              )}
              {!hideHowTheyScored && (
                <div className={clsx(styles.tsStatsCard, `pk-col pk-col--span-md-${gridSize} pk-col--span-12`)}>
                  <HowTheyScored data={howTheyScoredData} />
                </div>
              )}
              {!options?.hidePosition && (
                <div className={clsx(styles.tsStatsCard, `pk-col pk-col--span-md-${gridSize} pk-col--span-12`)}>
                  <EnhancedStatisticsModule data={{ matchesData, avgGoalData, goalCoordinates }} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayerData;
